import React from 'react';
import {
  Copy,
  Plus,
  CheckSquare,
  Square,
  Filter,
  ArrowUpDown,
  Package,
} from 'lucide-react';

const OrdersTab = ({
  orders,
  filteredOrders,
  phases,
  orderTemplates,
  orderStatusFilter,
  setOrderStatusFilter,
  orderVendorFilter,
  setOrderVendorFilter,
  orderVendors,
  orderSort,
  setOrderSort,
  selectedOrders,
  setSelectedOrders,
  toggleOrderSelection,
  toggleSelectAll,
  bulkStatus,
  setBulkStatus,
  handleBulkStatusUpdate,
  setShowNewOrder,
  setShowTemplatesPicker,
  setShowOrderDetail,
  formatCurrency,
  formatDate,
  getOrderStatusClass,
}) => {
  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <h2 className="font-heading text-lg font-bold uppercase tracking-wide">Material Orders</h2>
        <div className="flex items-center gap-2">
          {orderTemplates.length > 0 && (
            <button
              onClick={() => setShowTemplatesPicker(true)}
              data-testid="from-template-btn"
              className="h-12 px-4 bg-zinc-900 text-white font-bold uppercase tracking-wide border border-zinc-200 hover:bg-zinc-800 active:translate-y-0.5 transition-all flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              From Template
            </button>
          )}
          <button
            onClick={() => setShowNewOrder(true)}
            data-testid="new-order-btn"
            className="h-12 px-4 bg-primary text-zinc-900 font-bold uppercase tracking-wide border border-zinc-200  hover:bg-primary-hover active:translate-y-1 active: transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Order
          </button>
        </div>
      </div>

      {/* Filters and Sort */}
      {orders.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <button
            onClick={toggleSelectAll}
            data-testid="select-all-orders"
            className="w-10 h-10 border border-zinc-200 bg-white flex items-center justify-center hover:bg-zinc-50"
            title={selectedOrders.length === filteredOrders.length ? 'Deselect all' : 'Select all'}
          >
            {selectedOrders.length > 0 && selectedOrders.length === filteredOrders.length
              ? <CheckSquare className="w-5 h-5 text-primary" />
              : <Square className="w-5 h-5 text-zinc-400" />
            }
          </button>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <select
              value={orderStatusFilter}
              onChange={(e) => setOrderStatusFilter(e.target.value)}
              data-testid="order-status-filter"
              className="h-10 border border-zinc-200 bg-white text-sm font-bold px-3"
            >
              <option value="all">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Ordered">Ordered</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Shipped">Shipped</option>
              <option value="Delivered">Delivered</option>
              <option value="Issue">Issue</option>
            </select>
          </div>

          {orderVendors.length > 1 && (
            <select
              value={orderVendorFilter}
              onChange={(e) => setOrderVendorFilter(e.target.value)}
              data-testid="order-vendor-filter"
              className="h-10 border border-zinc-200 bg-white text-sm font-bold px-3"
            >
              <option value="all">All Vendors</option>
              {orderVendors.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          )}

          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-4 h-4 text-zinc-500" />
            <select
              value={orderSort}
              onChange={(e) => setOrderSort(e.target.value)}
              data-testid="order-sort"
              className="h-10 border border-zinc-200 bg-white text-sm font-bold px-3"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="amount-desc">Highest Amount</option>
              <option value="amount-asc">Lowest Amount</option>
            </select>
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedOrders.length > 0 && (
        <div className="bg-zinc-900 text-white p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
          <span className="text-sm font-bold">{selectedOrders.length} selected</span>
          <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
            data-testid="bulk-status-select"
            className="h-9 px-3 bg-zinc-800 text-white text-sm font-bold border border-zinc-600 focus:outline-none">
            <option value="">Set Status...</option>
            {['Draft','Ordered','Confirmed','Shipped','Delivered','Issue'].map(s =>
              <option key={s} value={s}>{s}</option>
            )}
          </select>
          <button onClick={handleBulkStatusUpdate} disabled={!bulkStatus}
            data-testid="bulk-update-btn"
            className={`h-9 px-4 font-bold uppercase text-xs ${
              bulkStatus ? 'bg-primary text-zinc-900 hover:bg-primary-hover' : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
            }`}>
            Update All
          </button>
          <button onClick={() => { setSelectedOrders([]); setBulkStatus(''); }}
            data-testid="bulk-clear-btn"
            className="h-9 px-3 text-xs font-bold text-zinc-400 hover:text-white">
            Clear
          </button>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center " data-testid="orders-empty-state">
          <Package className="w-16 h-16 mx-auto mb-4 text-zinc-500" />
          <p className="text-lg font-bold mb-2">No orders yet</p>
          <p className="text-zinc-500">Add material orders to track spend and delivery.</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center ">
          <p className="text-lg font-bold mb-2">No matching orders</p>
          <p className="text-zinc-500">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div
              key={order.order_id}
              data-testid={`order-card-${order.order_id}`}
              className="bg-white border border-zinc-200 p-4  hover: transition-shadow cursor-pointer flex gap-3"
            >
              <button
                onClick={(e) => { e.stopPropagation(); toggleOrderSelection(order.order_id); }}
                data-testid={`order-check-${order.order_id}`}
                className="flex-shrink-0 mt-1"
              >
                {selectedOrders.includes(order.order_id)
                  ? <CheckSquare className="w-5 h-5 text-primary" />
                  : <Square className="w-5 h-5 text-zinc-300 hover:text-zinc-500" />
                }
              </button>
              <div className="flex-1 min-w-0" onClick={() => setShowOrderDetail(order)}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-bold text-lg">{order.vendor_name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase border border-black ${getOrderStatusClass(order.status)}`}>
                        {order.status}
                      </span>
                      {order.po_number && (
                        <span className="text-xs font-mono bg-zinc-50 px-2 py-1 border border-black">
                          {order.po_number}
                        </span>
                      )}
                    </div>
                    {order.description && <p className="text-zinc-500 text-sm mb-2">{order.description}</p>}
                    <p className="text-xs text-zinc-500">
                      {order.line_items.length} item{order.line_items.length !== 1 ? 's' : ''} •
                      {formatDate(order.order_date || order.created_at)}
                      {order.phase_id && phases.find(p => p.phase_id === order.phase_id) && (
                        <> • Phase: {phases.find(p => p.phase_id === order.phase_id)?.name}</>
                      )}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <p className="text-2xl font-heading font-bold uppercase tracking-wide">{formatCurrency(order.total)}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default OrdersTab;
